import * as dayjs from 'dayjs';
import * as timezone from 'dayjs/plugin/timezone';
import * as utc from 'dayjs/plugin/utc';

// Load the plugins
dayjs.extend(utc);
dayjs.extend(timezone);

export enum DateFormats {
  Standard = 'mediumDate',
  DateAndTime = 'dd. MMM yyyy kl HH:mm', // 14. apr. 2022 kl 17:21 (norwegian)
  YearOnly = 'yyyy',
  MonthAndYear = 'MMM yyyy',
  Time = 'HH:mm',
  ShortFormFullDate = 'dd.MM.yyyy',
}

/**
 * We use 3000-01-01 in the DB for "forever".
 */
export const FOREVER = '3000-01-01';

export type DateRange = [Date | null, Date | null];

export function makeDateRange(fromDate: unknown, toDate: unknown): DateRange | null {
  const value = [fromDate, toDate];
  return isDateRangePair(value) ? value : null;
}

export function isDateRangePair(value: unknown): value is DateRange {
  if (Array.isArray(value) && value.length === 2 && value[0] !== undefined && value[1] !== undefined) {
    const [from, to] = value;
    return (from instanceof Date || from === null) && (to instanceof Date || to === null);
  }
  return false;
}

export function isDateString(val: unknown): val is string {
  // the first part of the ISO_8601 default format we use across the app
  const basicDateRegex = /^\d{4}-\d{2}-\d{2}/;
  // the last part checks if date is valid (there is no 33 days in a month or something)
  return typeof val === 'string' && basicDateRegex.test(val) && !Number.isNaN(new Date(val).getTime());
}

//  We only care about day, month and year in this case. Other options can be added later
type DayjsDateFormat = 'DD/MM/YYYY';

export function isSameDate(dateOne: Date, dateTwo: Date, dateFormat: DayjsDateFormat = 'DD/MM/YYYY'): boolean {
  return dayjs(dateOne).format(dateFormat) === dayjs(dateTwo).format(dateFormat);
}

/**
 * Adjusts a given date to UTC and returns the adjusted date as an ISO string.
 *
 * This function takes a JavaScript Date object, determines the local timezone offset,
 * converts the date to UTC, adjusts the time by subtracting the detected timezone offset,
 * and returns the adjusted date formatted as an ISO string.
 *
 * The reason is that we use UTC all across the database, while dates created on the frontend side can have a timezone offset.
 */
export function adjustDateToUTC(date: Date): string {
  const offsetInMinutes = date.getTimezoneOffset();
  const offsetInHours = offsetInMinutes / 60;
  return dayjs(date).utc().subtract(offsetInHours, 'hour').format();
}
